import { useState, useEffect, useRef } from "react";
import { X, ChevronLeft } from "lucide-react";
import { sizedImageUrl, sizedImageSrcSet } from "@/lib/img";

// Photo tour: category thumbnails are small (2-7 per row); large photos span
// the full modal width (single/hero) or half of it (two-up grids).
const THUMB_IMG_WIDTHS = [400, 800];
const THUMB_IMG_SIZES = "(min-width: 1024px) 15vw, (min-width: 640px) 30vw, 50vw";
const LARGE_IMG_WIDTHS = [800, 1600, 2000];
const FULL_IMG_SIZES = "100vw";
const HALF_IMG_SIZES = "(min-width: 768px) 50vw, 100vw";

interface PhotoCategory {
  name: string;
  images: string[];
}

interface PhotoTourModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  propertyName: string;
  categories?: PhotoCategory[];
}

// Category priority order - matches the backend sorting
const CATEGORY_PRIORITY_ORDER = [
  "Living room",
  "Bedroom",
  "Full bathroom",
  "Hot tub",
  "Exterior",
  "Patio",
  "Backyard",
  "Beach",
  "Golf course",
  "Full kitchen",
  "Dining area",
  "Office",
  "Garage",
  "Laundry area",
  "Additional photos"
];

// Sort categories by priority order
function sortCategoriesByPriority(categories: PhotoCategory[]): PhotoCategory[] {
  return [...categories].sort((a, b) => {
    const aIndex = CATEGORY_PRIORITY_ORDER.findIndex(cat => 
      a.name.toLowerCase().includes(cat.toLowerCase()) || cat.toLowerCase().includes(a.name.toLowerCase())
    );
    const bIndex = CATEGORY_PRIORITY_ORDER.findIndex(cat => 
      b.name.toLowerCase().includes(cat.toLowerCase()) || cat.toLowerCase().includes(b.name.toLowerCase())
    );
    const aPriority = aIndex === -1 ? CATEGORY_PRIORITY_ORDER.length : aIndex;
    const bPriority = bIndex === -1 ? CATEGORY_PRIORITY_ORDER.length : bIndex;
    return aPriority - bPriority;
  });
}

// Manual categorization mapping for specific property images
function categorizeImages(images: string[]): PhotoCategory[] {
  // Custom mapping for specific image URLs
  const customMapping: { [key: string]: string } = {
    "https://cdn.filestackcontent.com/lEw8LuKGSfK857tS2gBP": "Living room"
  };
  
  const categoryMap: { [key: string]: string[] } = {
    "Living room": [],
    "Full kitchen": [],
    "Dining area": [],
    "Bedroom": [],
    "Full bathroom": [],
    "Office": [],
    "Backyard": [],
    "Beach": [],
    "Golf course": [],
    "Patio": [],
    "Laundry area": [],
    "Garage": [],
    "Exterior": [],
    "Hot tub": [],
    "Additional photos": []
  };
  
  // First pass: assign images based on custom mapping
  const assignedImages = new Set<string>();
  
  images.forEach(imageUrl => {
    if (customMapping[imageUrl]) {
      categoryMap[customMapping[imageUrl]].push(imageUrl);
      assignedImages.add(imageUrl);
    }
  });
  
  // Second pass: assign remaining images using default logic
  const remainingImages = images.filter(img => !assignedImages.has(img));
  let currentIndex = 0;
  
  const categoryOrder = [
    { name: "Living room", maxImages: 4 },
    { name: "Full kitchen", maxImages: 3 },
    { name: "Dining area", maxImages: 2 },
    { name: "Bedroom", maxImages: 3 },
    { name: "Full bathroom", maxImages: 2 },
    { name: "Office", maxImages: 2 },
    { name: "Backyard", maxImages: 2 },
    { name: "Beach", maxImages: 2 },
    { name: "Golf course", maxImages: 2 },
    { name: "Patio", maxImages: 2 },
    { name: "Laundry area", maxImages: 2 },
    { name: "Garage", maxImages: 2 },
    { name: "Exterior", maxImages: 3 },
    { name: "Hot tub", maxImages: 2 }
  ];
  
  categoryOrder.forEach(({ name, maxImages }) => {
    const currentCount = categoryMap[name].length;
    const availableSlots = maxImages - currentCount;
    
    if (availableSlots > 0 && currentIndex < remainingImages.length) {
      const imagesToAdd = remainingImages.slice(currentIndex, currentIndex + availableSlots);
      categoryMap[name].push(...imagesToAdd);
      currentIndex += imagesToAdd.length;
    }
  });
  
  // Add any remaining images to "Additional photos"
  if (currentIndex < remainingImages.length) {
    categoryMap["Additional photos"].push(...remainingImages.slice(currentIndex));
  }
  
  // Convert to PhotoCategory array, filtering out empty categories
  const categories: PhotoCategory[] = [];
  Object.entries(categoryMap).forEach(([name, images]) => {
    if (images.length > 0) {
      categories.push({ name, images });
    }
  });
  
  return categories;
}

export default function PhotoTourModal({ isOpen, onClose, images, propertyName, categories }: PhotoTourModalProps) {
  const [activeCategory, setActiveCategory] = useState<string>("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Use categorized photos from database if available, otherwise fallback to auto-categorization
  // Sort categories by priority order (Living room first, then Bedroom, Bathroom, Hot tub, etc.)
  const unsortedCategories = categories || categorizeImages(images);
  const photoCategories = sortCategoriesByPriority(unsortedCategories);

  // Set initial active category
  useEffect(() => {
    if (photoCategories.length > 0 && !activeCategory) {
      setActiveCategory(photoCategories[0].name);
    }
  }, [photoCategories, activeCategory]);

  // Scroll to category when clicked
  const scrollToCategory = (categoryName: string) => {
    const categoryElement = categoryRefs.current[categoryName];
    if (categoryElement) {
      categoryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveCategory(categoryName);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Handle scroll to update active category
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => {
      const scrollTop = scrollContainerRef.current?.scrollTop || 0;
      const viewportHeight = window.innerHeight;
      const triggerPoint = scrollTop + viewportHeight / 3;

      for (const category of photoCategories) {
        const element = categoryRefs.current[category.name];
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;
          
          if (triggerPoint >= elementTop && triggerPoint < elementBottom) {
            setActiveCategory(category.name);
            break;
          }
        }
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [isOpen, photoCategories]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-background border-b border-border px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Mobile: Back to top on left, Desktop: Photo tour title */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
              className="md:hidden text-[#AD6433] hover:text-[#8B5025] font-medium transition-colors text-[16px]"
            >
              ↑ Back to top
            </button>
            <h2 className="hidden md:block text-xl font-semibold">Photo tour</h2>
          </div>
          {/* Mobile: X close button, Desktop: Back to top + X */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
              className="hidden md:block text-[#AD6433] hover:text-[#8B5025] font-medium transition-colors text-[16px]"
            >
              ↑ Back to top
            </button>
            <button
              onClick={onClose}
              className="rounded-sm border border-[#AD6433] p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </div>
      </div>
      {/* Scrollable Content */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-4 md:px-6 py-6 md:py-8"
        style={{ height: 'calc(100vh - 80px)' }}
      >
        {/* Top categories grid - responsive with consistent thumbnail size */}
        <div className="mb-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 md:gap-4">
            {photoCategories.slice(0, 14).map((category) => {
              const categoryImage = category.images[0];
              return (
                <button
                  key={category.name}
                  onClick={() => scrollToCategory(category.name)}
                  className="text-center transition-all hover:opacity-80"
                >
                  <div className="w-full aspect-[4/3] rounded-lg overflow-hidden mb-2">
                    <img
                      src={sizedImageUrl(categoryImage, 400)}
                      srcSet={sizedImageSrcSet(categoryImage, THUMB_IMG_WIDTHS)}
                      sizes={THUMB_IMG_SIZES}
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-xs md:text-sm font-medium text-foreground">{category.name}</div>
                </button>
              );
            })}
          </div>
        </div>

        {photoCategories.map((category) => (
          <div
            key={category.name}
            ref={(el) => categoryRefs.current[category.name] = el}
            className="mb-8 md:mb-12 last:mb-0"
          >
            {/* Category Title */}
            <h3 className="text-xl md:text-2xl font-semibold mb-3 md:mb-4">{category.name}</h3>
            
            {/* Photos Grid */}
            <div className="space-y-3 md:space-y-4">
              {category.images.length === 1 && (
                // Single large photo
                (<div className="w-full aspect-[4/3] md:aspect-[3/2] rounded-lg overflow-hidden">
                  <img
                    src={sizedImageUrl(category.images[0], 1600)}
                    srcSet={sizedImageSrcSet(category.images[0], LARGE_IMG_WIDTHS)}
                    sizes={FULL_IMG_SIZES}
                    alt={`${propertyName} - ${category.name}`}
                    className="w-full h-full object-cover"
                  />
                </div>)
              )}
              
              {category.images.length === 2 && (
                // Two photos side by side
                (<div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  {category.images.map((image, index) => (
                    <div key={index} className="aspect-[4/3] md:aspect-[3/2] rounded-lg overflow-hidden">
                      <img
                        src={sizedImageUrl(image, 800)}
                        srcSet={sizedImageSrcSet(image, LARGE_IMG_WIDTHS)}
                        sizes={HALF_IMG_SIZES}
                        alt={`${propertyName} - ${category.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>)
              )}
              
              {category.images.length >= 3 && (
                // Hero photo + smaller photos below
                (<>
                  {/* Hero photo */}
                  <div className="w-full aspect-[4/3] md:aspect-[3/2] rounded-lg overflow-hidden">
                    <img
                      src={sizedImageUrl(category.images[0], 1600)}
                      srcSet={sizedImageSrcSet(category.images[0], LARGE_IMG_WIDTHS)}
                      sizes={FULL_IMG_SIZES}
                      alt={`${propertyName} - ${category.name}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Additional photos */}
                  {category.images.length > 1 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      {category.images.slice(1).map((image, index) => (
                        <div key={index + 1} className="aspect-[4/3] md:aspect-[3/2] rounded-lg overflow-hidden">
                          <img
                            src={sizedImageUrl(image, 800)}
                            srcSet={sizedImageSrcSet(image, LARGE_IMG_WIDTHS)}
                            sizes={HALF_IMG_SIZES}
                            alt={`${propertyName} - ${category.name} ${index + 2}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>)
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}